import React, { useState, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Smile } from 'lucide-react';

/**
 * Composant RichTextEditor avec compteur de caractères Instagram
 * Limite : 2200 caractères (limite Instagram)
 */
const RichTextEditor = ({ value, onChange, placeholder = "Rédigez votre caption...", maxLength = 2200, className = "" }) => {
  const [charCount, setCharCount] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const quillRef = useRef(null);
  const emojiPickerRef = useRef(null);

  // Calculer le nombre de caractères (texte brut sans HTML)
  const getTextLength = (html) => {
    if (!html) return 0;
    // Créer un élément temporaire pour extraire le texte
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Mettre à jour le compteur quand la valeur change
  useEffect(() => {
    const textLength = getTextLength(value).length;
    setCharCount(textLength);
    setShowWarning(textLength > maxLength * 0.9); // Afficher un avertissement à 90%
  }, [value, maxLength]);

  // Liste d'emojis populaires pour Instagram
  const emojiCategories = {
    'Sourires': ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙'],
    'Émotions': ['😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥'],
    'Geste': ['👋', '🤚', '🖐', '✋', '🖖', '👌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎'],
    'Radio/Média': ['📻', '🎙️', '🎤', '🎧', '🎵', '🎶', '📢', '📣', '🔊', '🔉', '🔈', '📺', '🎬', '🎭', '🎨', '🎪', '🎯', '🎲', '🎮', '🎰'],
    'Symboles': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️'],
    'Divers': ['✨', '⭐', '🌟', '💫', '🔥', '💥', '💢', '💯', '💨', '💦', '💤', '🎉', '🎊', '🎈', '🎁', '🏆', '🥇', '🥈', '🥉', '🏅']
  };

  // Fermer le sélecteur d'emojis si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        // Vérifier si le clic n'est pas sur le bouton emoji
        const toolbar = document.querySelector('.ql-toolbar');
        if (toolbar && !toolbar.contains(event.target)) {
          setShowEmojiPicker(false);
        }
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showEmojiPicker]);

  // Insérer un emoji à la position du curseur
  const insertEmoji = (emoji) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      if (range) {
        quill.insertText(range.index, emoji, 'user');
        quill.setSelection(range.index + emoji.length);
      } else {
        // Si pas de sélection, insérer à la fin
        const length = quill.getLength();
        quill.insertText(length - 1, emoji, 'user');
        quill.setSelection(length - 1 + emoji.length);
      }
    }
    setShowEmojiPicker(false);
  };

  // Configuration de la toolbar personnalisée
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ],
  };

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'align', 'link'
  ];

  const handleChange = (content, delta, source, editor) => {
    const textLength = getTextLength(content).length;
    
    // Limiter à maxLength caractères
    if (textLength > maxLength) {
      // Extraire le texte et le tronquer
      const text = editor.getText();
      const truncatedText = text.substring(0, maxLength);
      
      // Mettre à jour l'éditeur avec le texte tronqué
      const selection = editor.getSelection();
      editor.setText(truncatedText);
      
      // Restaurer la position du curseur
      if (selection) {
        editor.setSelection(selection.index > truncatedText.length 
          ? truncatedText.length 
          : selection.index);
      }
      
      // Recalculer avec le contenu tronqué
      const finalLength = truncatedText.length;
      setCharCount(finalLength);
      setShowWarning(false);
      
      // Appeler onChange avec le contenu tronqué
      onChange(editor.root.innerHTML);
    } else {
      setCharCount(textLength);
      setShowWarning(textLength > maxLength * 0.9);
      onChange(content);
    }
  };

  const getCharCountColor = () => {
    if (charCount > maxLength) {
      return 'text-red-600';
    } else if (charCount > maxLength * 0.9) {
      return 'text-yellow-600';
    }
    return 'text-gray-500';
  };

  // Ajouter le bouton emoji à la toolbar après le montage
  useEffect(() => {
    if (quillRef.current) {
      const toolbar = quillRef.current.getEditor().getModule('toolbar');
      const toolbarElement = toolbar.container;
      
      // Créer le bouton emoji
      const emojiButton = document.createElement('button');
      emojiButton.type = 'button';
      emojiButton.className = 'ql-emoji';
      emojiButton.innerHTML = '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>';
      emojiButton.title = 'Insérer un emoji';
      emojiButton.addEventListener('click', (e) => {
        e.preventDefault();
        setShowEmojiPicker(!showEmojiPicker);
      });
      
      // Ajouter un séparateur
      const separator = document.createElement('span');
      separator.className = 'ql-formats';
      separator.style.borderLeft = '1px solid #ccc';
      separator.style.marginLeft = 'auto';
      separator.style.paddingLeft = '8px';
      
      separator.appendChild(emojiButton);
      toolbarElement.appendChild(separator);
      
      return () => {
        // Nettoyage si nécessaire
        if (separator.parentNode) {
          separator.parentNode.removeChild(separator);
        }
      };
    }
  }, []);

  return (
    <div className={`rich-text-editor relative ${className}`}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        style={{
          backgroundColor: 'white',
          borderRadius: '0.375rem',
        }}
      />
      
      {/* Sélecteur d'emojis positionné en absolu */}
      {showEmojiPicker && (
        <div
          ref={emojiPickerRef}
          className="absolute z-50 mt-2 bg-white border border-gray-300 rounded-lg shadow-xl"
          style={{ 
            top: '100%', 
            right: '0',
            maxWidth: '400px', 
            maxHeight: '400px', 
            overflowY: 'auto',
            marginTop: '8px'
          }}
        >
          <div className="p-3">
            {Object.entries(emojiCategories).map(([category, emojis]) => (
              <div key={category} className="mb-4">
                <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase">{category}</h4>
                <div className="grid grid-cols-10 gap-1">
                  {emojis.map((emoji, index) => (
                    <button
                      key={`${category}-${index}`}
                      type="button"
                      onClick={() => insertEmoji(emoji)}
                      className="w-8 h-8 flex items-center justify-center text-lg hover:bg-blue-100 rounded transition-colors hover:scale-110"
                      title={emoji}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Compteur de caractères et avertissement */}
      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {showWarning && (
            <span className="text-xs text-yellow-600">
              ⚠️ Approche de la limite Instagram
            </span>
          )}
          {charCount > maxLength && (
            <span className="text-xs text-red-600 font-bold">
              ❌ Limite dépassée !
            </span>
          )}
        </div>
        <span className={`text-xs font-medium ${getCharCountColor()}`}>
          {charCount} / {maxLength} caractères
        </span>
      </div>
      
      {/* Style personnalisé pour Quill */}
      <style jsx global>{`
        .rich-text-editor .ql-container {
          min-height: 150px;
          font-size: 14px;
        }
        .rich-text-editor .ql-editor {
          min-height: 150px;
        }
        .rich-text-editor .ql-toolbar {
          border-top-left-radius: 0.375rem;
          border-top-right-radius: 0.375rem;
          border-bottom: 1px solid #e5e7eb;
        }
        .rich-text-editor .ql-container {
          border-bottom-left-radius: 0.375rem;
          border-bottom-right-radius: 0.375rem;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;

